import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import * as bookingApi from "./bookingApi";
import { offsetName, whenLabel, zoneCity } from "./format";
import { AgendaChoices, AgendaLines, Divider, GuestBox, Mono, NoteBox, Page, PreviewBar } from "./parts";
import { LinkNotFound } from "./ManagePage";
import { Logo, readKind, useWide } from "./desktop";
import { mapsHref, placeLine } from "./WhereStep";

/** In person: the address, linked to a map, where the Meet link would be. */
function Where({ location }) {
  if (!location) return null;
  return (
    <p className="bk-strong">
      <a className="bk-meet" href={mapsHref(location)} target="_blank" rel="noreferrer">
        {placeLine(location)}
      </a>
      {location.note ? <span className="bk-muted"> · {location.note}</span> : null}
    </p>
  );
}

/** A small "Copy" beside the Meet link on desktop. */
function CopyMeet({ url }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="bkd-copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* blocked: the link is right there to copy by hand */
        }
      }}
    >
      {done ? "Copied" : "Copy"}
    </button>
  );
}

/*
 * /book/booked/:token — D4. THE BOOKING IS ALREADY DONE by the time this
 * renders: the time, the Meet link and the Google invite line come first, and
 * only then "What's this call for?", "Anything you want to add or ask?" and
 * "Anyone else joining?". Each saves on its own; leaving without touching any
 * of them is fine.
 *
 * Its own URL so a refresh does not land the lead back on the picker, where
 * they might book twice. The booking result rides in on navigation state; a
 * refresh re-reads it with the token.
 */
export default function BookedPage() {
  const { token } = useParams();
  const location = useLocation();
  // The CRM's Page-tab preview renders this screen from a sample, and saves nothing.
  const preview = token === "preview";
  const previewQuery = new URLSearchParams(location.search);
  const previewPass = previewQuery.get("p");
  const previewTz = previewQuery.get("tz");
  const previewAs = previewQuery.get("as");
  const previewType = previewQuery.get("type");
  const handed = location.state && location.state.booking;
  const moved = !!(location.state && location.state.moved);

  const [booking, setBooking] = useState(handed || null);
  const [company, setCompany] = useState((handed && handed.company) || null);
  const [agendas, setAgendas] = useState((handed && handed.agendas) || null);
  // "Before we talk": set on the CRM's booking page. Empty hides the section.
  const [reads, setReads] = useState((handed && handed.before_we_talk) || []);
  // Which meeting this is (intro call, friendly chat, …), for the labels.
  const [mtype, setMtype] = useState((handed && handed.type) || null);
  const [intent, setIntent] = useState((handed && (handed.intent || handed.suggested_intent)) || null);
  const [missing, setMissing] = useState(false);
  const autoSaved = useRef(false);
  const wide = useWide();

  useEffect(() => {
    if (handed && handed.agendas) return;
    (preview
      ? bookingApi.getPreviewSuccess({ preview: previewPass, tz: previewTz, as: previewAs, type: previewType }).then((sample) => ({ booking: sample, company: sample.company, agendas: sample.agendas, before_we_talk: sample.before_we_talk, type: sample.type }))
      : bookingApi.getManage({ token }))
      .then((data) => {
        setBooking(data.booking);
        setCompany(data.company);
        setAgendas(data.agendas);
        setReads(data.before_we_talk || []);
        setMtype(data.type || null);
        setIntent(data.booking.intent);
      })
      .catch((err) => {
        if (err.status === 404) setMissing(true);
      });
  }, [token, handed, preview, previewPass, previewTz, previewAs, previewType]);

  /*
   * A personal link's suggested agenda is shown selected, so it is saved as
   * selected: the invite should say what the page says. Once, and only when the
   * lead has not already chosen.
   */
  useEffect(() => {
    if (preview || autoSaved.current || !handed || handed.intent || !handed.suggested_intent) return;
    autoSaved.current = true;
    bookingApi.updateAgenda({ token, intent: handed.suggested_intent }).catch(() => {});
  }, [token, handed]);

  // A "Before we talk" page opens inside the app and knows its way back here.
  const readHref = (id) => {
    const params = preview
      ? { p: previewPass, ...(previewAs ? { as: previewAs } : {}), ...(previewTz ? { tz: previewTz } : {}) }
      : { b: token };
    return `/book/read/${encodeURIComponent(id)}?${new URLSearchParams(params)}`;
  };

  if (missing) return <LinkNotFound />;
  if (!booking) return <Page><div className="bk-body"><p className="bk-muted">One moment…</p></div></Page>;

  const zone = booking.timezone;
  const chosen = agendas && agendas.find((a) => a.key === intent);
  // A second call: its agenda was written from the first call, nothing to pick.
  const followed = booking.follow_up ? booking.agenda : null;
  const minutes = Math.round((new Date(booking.end) - new Date(booking.start)) / 60000);

  const choose = async (key) => {
    setIntent(key);
    try {
      if (!preview) await bookingApi.updateAgenda({ token, intent: key });
    } catch {
      /* the choice stays on screen; the next change or the note retries */
    }
  };

  // The preview saves nothing, so a guest added there only lives on screen.
  const guestBox = (stacked = false) => (
    <GuestBox
      stacked={stacked}
      guests={booking.guests || []}
      onAdd={async (email) => {
        const next = preview
          ? { ...booking, guests: [...(booking.guests || []).filter((g) => g.email !== email.toLowerCase()), { email: email.toLowerCase(), name: null }] }
          : await bookingApi.addGuest({ token, email });
        setBooking((b) => ({ ...b, guests: next.guests }));
      }}
      onRemove={async (email) => {
        const next = preview
          ? { ...booking, guests: (booking.guests || []).filter((g) => g.email !== email) }
          : await bookingApi.removeGuest({ token, email });
        setBooking((b) => ({ ...b, guests: next.guests }));
      }}
    />
  );

  const note = (
    <NoteBox
      value={booking.note}
      placeholder="A question, a goal for the call, a link to an ad you want me to look at"
      onSave={(text) => (preview ? Promise.resolve() : bookingApi.updateAgenda({ token, note: text }))}
    />
  );

  /*
   * DESKTOP (Remotion_Booking_-_Desktop.pptx, 2A/2B): the green band carries
   * the time and the Meet card; the agenda and the question sit on the left,
   * "Before we talk" as cards on the right.
   */
  if (wide) {
    return (
      <Page>
        {preview ? <PreviewBar /> : null}
        <div className="bkd-bar">
          <div className="bkd-bar__left">
            <Logo />
          </div>
          <span className="bk-mono bkd-bar__right">
            {mtype && (!mtype.lead || mtype.follow_up) ? String(mtype.name).toUpperCase() : "INTRO CALL"} ·{" "}
            {Math.round((new Date(booking.end) - new Date(booking.start)) / 60000)} MIN
          </span>
        </div>
        <header className="bkd-green">
          <div>
            <Mono className="bk-kicker">
              ✓ {moved ? "Moved" : "Booked"}
              {company ? ` · ${company} × Remotion` : ""}
            </Mono>
            <h1 className="bkd-green__when">{whenLabel(booking.start, booking.end, zone)}</h1>
            <p className="bk-header__sub">
              {zoneCity(zone)} time ({offsetName(zone)})
            </p>
          </div>
          <div className="bkd-meet">
            {booking.meet_url ? (
              <div className="bkd-meet__row">
                <a className="bk-meet" href={booking.meet_url} target="_blank" rel="noreferrer">
                  {booking.meet_url.replace(/^https?:\/\//, "")}
                </a>
                <CopyMeet url={booking.meet_url} />
              </div>
            ) : null}
            <Where location={booking.location} />
            <p>{moved ? "Google is sending everyone the new time." : "Your invite is on its way from Google."}</p>
          </div>
        </header>
        <div className="bkd-body bkd-cols bkd-cols--success">
          <div className="bkd-main">
            {agendas && agendas.length ? (
              <>
                <div className="bkd-h2row">
                  <h2>What's this call for?</h2>
                  <span className="bk-muted">{intent ? "Tap another to switch · saves on its own" : "Optional · saves on its own"}</span>
                </div>
                <AgendaChoices agendas={agendas} value={intent} onChange={choose} />
                {chosen ? (
                  <div className="bkd-agenda">
                    <Mono className="bk-muted">The 30 minutes</Mono>
                    <AgendaLines agenda={chosen} />
                  </div>
                ) : null}
              </>
            ) : followed ? (
              <>
                <div className="bkd-h2row">
                  <h2>What we'll cover</h2>
                </div>
                <div className="bkd-agenda">
                  <Mono className="bk-muted">The {minutes} minutes</Mono>
                  <AgendaLines agenda={followed} />
                </div>
              </>
            ) : null}
            <div className="bkd-h2row">
              <h2>Anything you want to add or ask?</h2>
              <span className="bk-muted">Optional</span>
            </div>
            {note}
          </div>
          <aside className="bkd-side">
            {/* Beside the invite it adds to, above the fold. */}
            <Mono>Anyone else joining? · Optional</Mono>
            <p className="bk-muted bkd-guests-hint">Add a colleague and Google sends them the invite.</p>
            {guestBox(true)}
            {reads.length ? (
              <>
                <Mono>Before we talk · Optional</Mono>
                {reads.map((item) => (
                  <Link key={item.id} className="bkd-readcard" to={readHref(item.id)}>
                    <Mono className={item.video ? "bkd-readcard__kind is-video" : "bkd-readcard__kind"}>{readKind(item)}</Mono>
                    <b>{item.title}</b>
                    {item.teaser ? <span>{item.teaser}</span> : null}
                    <strong>Read →</strong>
                  </Link>
                ))}
              </>
            ) : null}
            <p className="bk-muted">Need to move or cancel? Use the link in your invite from Google.</p>
          </aside>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      {preview ? <PreviewBar /> : null}
      <header className="bk-header bk-header--green">
        <Mono className="bk-kicker">
          ✓ {moved ? "Moved" : "Booked"}
          {company ? ` · ${company} × Remotion` : ""}
        </Mono>
        <h1 className="bk-headline">{whenLabel(booking.start, booking.end, zone)}</h1>
        <p className="bk-header__sub">
          {zoneCity(zone)} time ({offsetName(zone)})
        </p>
      </header>

      <div className="bk-body">
        <div className="bk-card">
          {booking.meet_url ? (
            <a className="bk-meet" href={booking.meet_url} target="_blank" rel="noreferrer">
              {booking.meet_url.replace(/^https?:\/\//, "")}
            </a>
          ) : null}
          <Where location={booking.location} />
          <p>{moved ? "Google is sending everyone the new time." : "Your invite is on its way from Google."}</p>
        </div>

        <Divider />

        {agendas && agendas.length ? (
          <>
            <div className="bk-row">
              <Mono>What's this call for?</Mono>
              <span className="bk-muted">{intent ? "Tap another to switch" : "Optional"}</span>
            </div>
            <AgendaChoices agendas={agendas} value={intent} onChange={choose} />
            {chosen ? <AgendaLines agenda={chosen} /> : null}
          </>
        ) : followed ? (
          <>
            <div className="bk-row">
              <Mono>What we'll cover</Mono>
            </div>
            <AgendaLines agenda={followed} />
          </>
        ) : null}

        <div className="bk-row bk-row--label">
          <h2 className="bk-h2">Anything you want to add or ask?</h2>
          <span className="bk-muted">Optional</span>
        </div>
        {note}

        <div className="bk-row bk-row--label">
          <h2 className="bk-h2">Anyone else joining?</h2>
          <span className="bk-muted">Optional</span>
        </div>
        {guestBox()}

        {reads.length ? (
          <section className="bk-reads">
            <Mono className="bk-section">Before we talk · Optional</Mono>
            {reads.map((item) => (
              <Link key={item.id} className="bk-read" to={readHref(item.id)}>
                <span>
                  {item.title}
                  {item.teaser ? <small>{item.teaser}</small> : null}
                </span>
                <span aria-hidden="true">{item.video ? "▶" : "→"}</span>
              </Link>
            ))}
          </section>
        ) : null}
      </div>
    </Page>
  );
}
